import { io, Socket } from 'socket.io-client';
import { IMessagingProvider, IMessage } from '../IMessagingProvider';
import axios from 'axios';
import { NotificationService } from '../../../utils/notifications';
import { encryptMessage, decryptMessage, getKeyPair } from '../../encryption';

// Use REACT_APP_API_BASE_URL env var, defaults to empty string (relative URLs)
// For dev: set REACT_APP_API_BASE_URL=http://localhost:8080 in .env
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Server-side messaging provider - messages stored in MySQL database
export class ServerMessagingProvider implements IMessagingProvider {
  private socket: Socket | null = null;
  private currentUserId: string;
  private senderName: string = '';
  private messageCallbacks: Array<(msg: IMessage) => void> = [];
  private onlineStatusCallbacks: Array<(userId: string, isOnline: boolean) => void> = [];
  private onlineUsers: Set<string> = new Set();

  constructor(userId: string, senderName?: string) {
    this.currentUserId = userId;
    this.senderName = senderName || '';
  }

  onUserOnlineStatusChanged(callback: (userId: string, isOnline: boolean) => void): void {
    this.onlineStatusCallbacks.push(callback);
  }

  async connect(): Promise<void> {
    // Connect to signaling server
    this.socket = io(API_BASE_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    return new Promise((resolve, reject) => {
      if (!this.socket) return reject('Socket not initialized');

      this.socket.on('connect', () => {
        this.socket!.emit('join', this.currentUserId);
        this.setupSocketListeners();
        resolve();
      });

      // On reconnect, re-announce presence so the server map stays accurate
      this.socket.on('reconnect', () => {
        this.socket!.emit('join', this.currentUserId);
      });

      this.socket.on('connect_error', (error) => {
        console.error('Connection error:', error);
        reject(error);
      });
    });
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    // Listen for real-time messages
    this.socket.on('relayed-message', async ({ fromUserId, encryptedMessage, senderName, senderPublicKey }) => {
      // Decrypt the message if it's encrypted
      let message: IMessage = { ...encryptedMessage };

      if (encryptedMessage.encrypted && encryptedMessage.content) {
        try {
          const keyPair = getKeyPair();
          if (keyPair && senderPublicKey) {
            const decryptedContent = decryptMessage(
              encryptedMessage.content,
              senderPublicKey,
              keyPair.secretKey
            );
            message = {
              ...encryptedMessage,
              content: decryptedContent,
              encrypted: false
            };
          }
        } catch (error) {
          message.content = '🔒 Older message (sent before encryption keys were refreshed)';
        }
      }

      // Show browser notification if page not focused
      if (senderName) {
        await NotificationService.showMessageNotification(
          senderName,
          message.content,
          fromUserId
        );
      }

      this.messageCallbacks.forEach(callback => callback(message));
    });

    // Initial snapshot of who is online when we join
    this.socket.on('online-users', (userIds: string[]) => {
      this.onlineUsers.clear();
      userIds.forEach(id => {
        if (id !== this.currentUserId) {
          this.onlineUsers.add(id);
          this.onlineStatusCallbacks.forEach(cb => cb(id, true));
        }
      });
    });

    // Incremental updates
    this.socket.on('user-online', (userId: string) => {
      this.onlineUsers.add(userId);
      this.onlineStatusCallbacks.forEach(callback => callback(userId, true));
    });

    this.socket.on('user-offline', (userId: string) => {
      this.onlineUsers.delete(userId);
      this.onlineStatusCallbacks.forEach(callback => callback(userId, false));
    });
  }

  async sendMessage(recipientId: string, content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; linkPreview?: any }): Promise<IMessage> {
    // Encrypt message before sending
    let encryptedContent = content;
    let isEncrypted = false;
    let senderPublicKey = '';

    try {
      // Ensure local keys exist and are synced to server
      const { initializeEncryption } = await import('../../encryption');
      const myPublicKey = initializeEncryption();
      let keyPair = getKeyPair();

      // Always keep server in sync with our current local key
      try {
        await axios.put(`${API_BASE_URL}/api/auth/user/public-key`, { publicKey: myPublicKey }, { withCredentials: true });
      } catch (err) {
        console.warn('Failed to sync public key:', err);
      }

      // Get recipient's public key — only encrypt if they have one from their own device
      let recipientPublicKey: string | null = null;
      try {
        const recipientKeyResponse = await axios.get(
          `${API_BASE_URL}/api/auth/user/${recipientId}/public-key`,
          { withCredentials: true }
        );
        recipientPublicKey = recipientKeyResponse.data.publicKey || null;
      } catch (err) {
        console.warn('Failed to get recipient public key:', err);
      }

      if (keyPair && recipientPublicKey) {
        encryptedContent = encryptMessage(content, recipientPublicKey, keyPair.secretKey);
        isEncrypted = true;
        senderPublicKey = myPublicKey;
      }
      // If no recipient key: send as plaintext — readable without decryption
    } catch (error) {
      console.warn('Encryption unavailable, sending as plain text:', error);
    }

    const message: IMessage = {
      id: this.generateMessageId(),
      senderId: this.currentUserId,
      recipientId,
      content,
      timestamp: Date.now(),
      status: 'sending',
      encrypted: isEncrypted,
      ...(mediaData && {
        mediaType: mediaData.mediaType as 'image' | 'video' | 'link',
        mediaUrl: mediaData.mediaUrl,
        thumbnailUrl: mediaData.thumbnailUrl,
        linkPreview: mediaData.linkPreview
      })
    };

    try {
      // Store on server with encrypted content
      const response = await axios.post(
        `${API_BASE_URL}/api/messages`,
        {
          id: message.id,
          recipientId,
          content: encryptedContent,
          isEncrypted,
          ...(mediaData && {
            mediaType: mediaData.mediaType,
            mediaUrl: mediaData.mediaUrl,
            thumbnailUrl: mediaData.thumbnailUrl,
            linkPreview: mediaData.linkPreview
          })
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        message.status = 'sent';

        // Relay via WebSocket for real-time delivery
        if (this.socket && this.socket.connected) {
          this.socket.emit('relay-message', {
            recipientId,
            encryptedMessage: message,
            senderName: this.senderName
          });
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }

    return message;
  }

  async getMessages(recipientId: string): Promise<IMessage[]> {
    try {
      // Re-sync our public key so the other user always has the current one
      try {
        const { initializeEncryption } = await import('../../encryption');
        const myPublicKey = initializeEncryption();
        await axios.put(`${API_BASE_URL}/api/auth/user/public-key`, { publicKey: myPublicKey }, { withCredentials: true });
      } catch (e) { /* non-fatal */ }

      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversation/${recipientId}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        const keyPair = getKeyPair();

        // Get sender's public key for decryption
        let senderPublicKey: string | null = null;
        try {
          const keyResponse = await axios.get(
            `${API_BASE_URL}/api/auth/user/${recipientId}/public-key`,
            { withCredentials: true }
          );
          if (keyResponse.data.success) {
            senderPublicKey = keyResponse.data.publicKey;
          }
        } catch (error) {
          console.warn('Failed to get sender public key:', error);
        }

        return response.data.data.messages.map((msg: any) => {
          let content = msg.content;

          // Decrypt if message is encrypted and we have keys
          if (msg.is_encrypted && keyPair && senderPublicKey) {
            try {
              content = decryptMessage(msg.content, senderPublicKey, keyPair.secretKey);
            } catch (error) {
              content = '🔒 Older message (sent before encryption keys were refreshed)';
            }
          }

          return {
            id: msg.id,
            senderId: String(msg.sender_id),
            recipientId: String(msg.recipient_id),
            content,
            timestamp: new Date(
              typeof msg.created_at === 'string'
                ? msg.created_at.includes('Z') || msg.created_at.includes('+')
                  ? msg.created_at
                  : msg.created_at.replace(' ', 'T') + 'Z'
                : msg.created_at
            ).getTime(),
            status: msg.status,
            encrypted: false,
            ...(msg.expires_at && {
              expiresAt: new Date(
                typeof msg.expires_at === 'string'
                  ? msg.expires_at.includes('Z') || msg.expires_at.includes('+')
                    ? msg.expires_at
                    : msg.expires_at.replace(' ', 'T') + 'Z'
                  : msg.expires_at
              ).getTime()
            }),
            ...(msg.media_type && {
              mediaType: msg.media_type,
              mediaUrl: msg.media_url,
              thumbnailUrl: msg.thumbnail_url,
              linkPreview: msg.link_preview ? (typeof msg.link_preview === 'string' ? JSON.parse(msg.link_preview) : msg.link_preview) : undefined
            })
          };
        });
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      return [];
    }
  }

  async getAllConversations(): Promise<{ userId: string; lastMessage: IMessage | null; unreadCount: number }[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversations`,
        { withCredentials: true }
      );

      if (response.data.success) {
        return response.data.data.conversations.map((conv: any) => ({
          userId: String(conv.user_id),
          lastMessage: null, // We can fetch this separately if needed
          unreadCount: conv.unread_count || 0
        }));
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      return [];
    }
  }

  async markAsRead(messageId: string): Promise<void> {
    try {
      await axios.put(
        `${API_BASE_URL}/api/messages/read`,
        { messageIds: [messageId] },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  }

  async isOnline(userId: string): Promise<boolean> {
    return this.onlineUsers.has(userId);
  }

  async deleteConversation(userId: string): Promise<void> {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/messages/conversation/${userId}`,
        { withCredentials: true }
      );
    } catch (error: any) {
      const serverMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      console.error('Failed to delete conversation — server says:', serverMsg, error);
      throw error;
    }
  }

  onMessageReceived(callback: (message: IMessage) => void): void {
    this.messageCallbacks.push(callback);
  }

  async disconnect(): Promise<void> {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private generateMessageId(): string {
    return `${this.currentUserId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Required by interface but not used with server storage
  async getUserPublicKey(userId: string): Promise<string | null> {
    return null;
  }

  getPublicKey(): string {
    return '';
  }
}
